import { useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Mail, Check } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogTrigger } from "@/components/ui/dialog";
import { Reveal } from "@/components/Reveal";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const fmt = (n) => new Intl.NumberFormat("cs-CZ", { maximumFractionDigits: 0 }).format(Math.round(n));

const CalcReportDialog = ({ amount, rate, years, monthly }) => {
  const openedAt = useRef(Date.now());
  const [email, setEmail] = useState("");
  const [website, setWebsite] = useState("");
  const [sentTo, setSentTo] = useState("");
  const [error, setError] = useState("");
  const [status, setStatus] = useState("idle"); // idle | sending | sent | demo

  const onSubmit = async (e) => {
    e.preventDefault();
    setError("");
    if (!EMAIL_RE.test(email.trim())) {
      setError("Zadejte prosím platnou e-mailovou adresu.");
      return;
    }
    setStatus("sending");
    try {
      const res = await fetch(`${API}/calc-report`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email, website, amount, rate, years, elapsed_ms: Date.now() - openedAt.current }),
      });
      const data = await res.json().catch(() => ({}));
      if (res.ok && (data.status === "ok" || data.status === "demo")) {
        setSentTo(email.trim());
        setStatus("sent");
      } else {
        setStatus("idle");
        setError(typeof data.detail === "string" ? data.detail : "Přehled se nepodařilo odeslat. Zkuste to prosím znovu.");
      }
    } catch {
      setStatus("idle");
      setError("Přehled se nepodařilo odeslat. Zkontrolujte prosím připojení a zkuste to znovu.");
    }
  };

  return (
    <Dialog>
      <DialogTrigger asChild>
        <button
          type="button"
          data-testid="calc-send-result-button"
          className="group inline-flex items-center gap-2 text-xs uppercase tracking-widest font-medium text-[#252525] border-b border-[#B69A58] pb-1 hover:text-[#B69A58] transition-colors duration-300"
        >
          Poslat výsledek na e-mail
          <Mail className="h-4 w-4" strokeWidth={1.5} />
        </button>
      </DialogTrigger>
      <DialogContent className="bg-white sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl text-[#252525]">Výsledek na e-mail</DialogTitle>
          <DialogDescription className="text-sm text-[#4A4A4A]">
            Pošlu vám přehledný PDF výpočet s vašimi hodnotami a svým kontaktem.
          </DialogDescription>
        </DialogHeader>
        <p className="text-sm text-[#737373] border border-[#F0F0F0] bg-[#FAFAF8] p-4" data-testid="calc-report-summary">
          {fmt(amount)} Kč · {rate.toLocaleString("cs-CZ")} % · {years} let → <strong className="text-[#252525]">{fmt(monthly)} Kč měsíčně</strong>
        </p>
        {status === "sent" ? (
          <div role="status" data-testid="calc-report-success" className="mt-4 border border-[#F0F0F0] p-5">
            <div className="flex items-center gap-4">
              <Check className="h-5 w-5 text-[#B69A58] shrink-0" strokeWidth={1.5} aria-hidden="true" />
              <p className="text-sm text-[#252525]">
                Hotovo — přehled s výpočtem jsem odeslal na adresu <strong data-testid="calc-report-sent-to">{sentTo}</strong>.
              </p>
            </div>
            <p className="text-xs text-[#737373] leading-relaxed mt-3">
              Kdyby do pár minut nedorazil, podívejte se prosím do složky Spam nebo Hromadné — případně mi napište a pošlu ho znovu.
            </p>
          </div>
        ) : (
          <form onSubmit={onSubmit} noValidate data-testid="calc-report-form" className="mt-4">
            <label htmlFor="calc-report-email" className="block text-xs uppercase tracking-widest text-[#737373] font-medium mb-1">
              Váš e-mail *
            </label>
            <input
              id="calc-report-email"
              data-testid="calc-report-email-input"
              type="email"
              autoComplete="email"
              value={email}
              onChange={(e) => { setEmail(e.target.value); setError(""); }}
              className={`bg-transparent border-b ${error ? "border-red-700" : "border-[#E5E5E5]"} focus:border-[#B69A58] outline-none py-3 px-0 w-full text-base transition-colors duration-300 rounded-none`}
              aria-invalid={!!error}
            />
            {error && <p role="alert" data-testid="calc-report-error" className="text-sm text-red-700 mt-2">{error}</p>}
            <div className="absolute -left-[9999px] top-auto w-px h-px overflow-hidden" aria-hidden="true">
              <label htmlFor="calc-report-website">Webové stránky</label>
              <input
                id="calc-report-website"
                data-testid="calc-report-honeypot"
                type="text"
                tabIndex={-1}
                autoComplete="off"
                value={website}
                onChange={(e) => setWebsite(e.target.value)}
              />
            </div>
            <button
              type="submit"
              data-testid="calc-report-submit"
              disabled={status === "sending"}
              className="mt-6 w-full border border-[#B69A58] bg-white text-[#252525] hover:bg-[#B69A58] hover:text-white transition-colors duration-300 px-6 py-4 text-xs uppercase tracking-widest font-medium disabled:opacity-50"
            >
              {status === "sending" ? "Odesílám…" : "Odeslat přehled"}
            </button>
            <p className="text-xs text-[#737373] leading-relaxed mt-4">
              E-mail použiju výhradně k zaslání tohoto přehledu.{" "}
              <Link to="/ochrana-osobnich-udaju" data-testid="calc-report-privacy-link" className="underline underline-offset-2 hover:text-[#B69A58] transition-colors duration-300">
                Informace o zpracování osobních údajů
              </Link>
            </p>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
};

const SliderRow = ({ label, value, display, min, max, step, onChange, testId }) => (
  <div>
    <div className="flex items-baseline justify-between gap-4 mb-2">
      <label htmlFor={testId} className="text-xs uppercase tracking-widest text-[#737373] font-medium">
        {label}
      </label>
      <span className="font-display text-xl text-[#252525] whitespace-nowrap" data-testid={`${testId}-value`}>
        {display}
      </span>
    </div>
    <input
      id={testId}
      type="range"
      data-testid={testId}
      min={min}
      max={max}
      step={step}
      value={value}
      onChange={(e) => onChange(Number(e.target.value))}
      className="w-full accent-[#B69A58] h-1"
    />
  </div>
);

export const Calculator = () => {
  const [amount, setAmount] = useState(3500000);
  const [rate, setRate] = useState(4.5);
  const [years, setYears] = useState(30);

  const { monthly, total, interest } = useMemo(() => {
    const r = rate / 100 / 12;
    const n = years * 12;
    const m = r > 0 ? (amount * r * Math.pow(1 + r, n)) / (Math.pow(1 + r, n) - 1) : amount / n;
    return { monthly: m, total: m * n, interest: m * n - amount };
  }, [amount, rate, years]);

  return (
    <section id="kalkulacka" data-testid="calculator-section" className="py-24 md:py-32 border-t border-[#F0F0F0]">
      <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-20">
        <Reveal>
          <p className="flex items-center gap-4 text-xs uppercase tracking-[0.25em] text-[#737373] font-medium">
            <span className="w-10 h-[1px] bg-[#B69A58]" aria-hidden="true" />
            Hypoteční kalkulačka
          </p>
          <h2 className="font-display text-3xl md:text-4xl lg:text-5xl tracking-tight leading-[1.15] text-[#252525] mt-6 max-w-2xl">
            Kolik budu splácet?
          </h2>
          <p className="text-base md:text-lg text-[#4A4A4A] leading-relaxed mt-6 max-w-2xl">
            Pohněte posuvníky a orientačně si spočítejte měsíční splátku hypotéky.
            Přesnou nabídku vždy určí banka — tu vám pomohu vyjednat.
          </p>
        </Reveal>

        <div className="grid lg:grid-cols-2 gap-12 lg:gap-20 mt-14">
          <Reveal className="flex flex-col">
            <div className="space-y-10">
              <SliderRow
                label="Výše úvěru"
                value={amount}
                display={`${fmt(amount)} Kč`}
                min={500000}
                max={15000000}
                step={100000}
                onChange={setAmount}
                testId="calc-amount"
              />
              <SliderRow
                label="Úroková sazba"
                value={rate}
                display={`${rate.toLocaleString("cs-CZ")} %`}
                min={2}
                max={8}
                step={0.1}
                onChange={setRate}
                testId="calc-rate"
              />
              <SliderRow
                label="Doba splácení"
                value={years}
                display={`${years} let`}
                min={5}
                max={40}
                step={1}
                onChange={setYears}
                testId="calc-years"
              />
            </div>

            <div className="border border-[#E5E5E5] bg-[#FAFAF8] p-6 md:p-8 mt-10 flex-1" data-testid="calc-factors">
              <p className="text-xs uppercase tracking-widest text-[#737373] font-medium">Co ještě ovlivní splátku</p>
              <ul className="mt-5 space-y-4">
                <li className="flex gap-4">
                  <span className="w-4 h-[1px] bg-[#B69A58] shrink-0 mt-3" aria-hidden="true" />
                  <p className="text-sm text-[#4A4A4A] leading-relaxed">
                    <strong className="font-medium text-[#252525]">Vlastní zdroje</strong> — banky financují obvykle 80–90 % hodnoty nemovitosti
                  </p>
                </li>
                <li className="flex gap-4">
                  <span className="w-4 h-[1px] bg-[#B69A58] shrink-0 mt-3" aria-hidden="true" />
                  <p className="text-sm text-[#4A4A4A] leading-relaxed">
                    <strong className="font-medium text-[#252525]">Délka fixace</strong> — kratší fixace znamená dřívější změnu podmínek, delší zase delší klid
                  </p>
                </li>
                <li className="flex gap-4">
                  <span className="w-4 h-[1px] bg-[#B69A58] shrink-0 mt-3" aria-hidden="true" />
                  <p className="text-sm text-[#4A4A4A] leading-relaxed">
                    <strong className="font-medium text-[#252525]">Vaše bonita</strong> — příjmy a další závazky rozhodují o finální sazbě
                  </p>
                </li>
              </ul>
            </div>
          </Reveal>

          <Reveal delay={0.1} className="flex">
            <div className="relative border border-[#E5E5E5] bg-white p-8 md:p-12 flex-1 flex flex-col" data-testid="calc-result">
              <div className="absolute top-0 left-8 w-16 h-[1px] bg-[#B69A58]" aria-hidden="true" />
              <p className="text-xs uppercase tracking-[0.25em] text-[#737373] font-medium">Měsíční splátka</p>
              <p className="font-display text-5xl md:text-6xl text-[#252525] mt-4" data-testid="calc-monthly">
                {fmt(monthly)} <span className="text-2xl text-[#737373]">Kč</span>
              </p>
              <div className="w-12 h-[1px] bg-[#B69A58] my-8" aria-hidden="true" />
              <dl className="space-y-4">
                <div className="flex justify-between gap-4">
                  <dt className="text-sm text-[#737373]">Celkem zaplatíte</dt>
                  <dd className="text-sm font-medium text-[#252525]" data-testid="calc-total">{fmt(total)} Kč</dd>
                </div>
                <div className="flex justify-between gap-4">
                  <dt className="text-sm text-[#737373]">Z toho úroky</dt>
                  <dd className="text-sm font-medium text-[#252525]" data-testid="calc-interest">{fmt(interest)} Kč</dd>
                </div>
              </dl>
              <p className="text-xs text-[#737373] leading-relaxed mt-8 mb-8">
                Orientační výpočet anuitní splátky. Nezahrnuje poplatky, pojištění ani aktuální nabídky bank.
              </p>
              <a
                href="/#termin"
                data-testid="calc-cta"
                className="group mt-auto inline-flex items-center gap-2 border border-[#B69A58] bg-white text-[#252525] hover:bg-[#B69A58] hover:text-white transition-colors duration-300 px-8 py-4 text-xs uppercase tracking-widest font-medium self-start"
              >
                Spočítat přesně s Danielem
                <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" strokeWidth={1.5} />
              </a>
              <div className="mt-6">
                <CalcReportDialog amount={amount} rate={rate} years={years} monthly={monthly} />
              </div>
            </div>
          </Reveal>
        </div>
      </div>
    </section>
  );
};
